import React, { useState, useEffect, useRef } from 'react';
import { Button, Form, Spinner, Alert, Modal } from 'react-bootstrap';
import { apiService } from '../../services/api';
import './FullPageChatRoom.scss';

interface FullPageChatRoomProps {
  chatRoom: {
    id: number;
    name: string;
    content_object: string;
    is_public: boolean;
  };
  onBack?: () => void;
}

interface Message {
  id: number;
  content: string;
  message_type: 'text' | 'image' | 'file';
  is_system_message: boolean;
  sender?: {
    id: number;
    username: string;
    full_name: string;
  };
  is_outgoing: boolean;
  created_at: string;
  time_ago: string;
  file_url?: string;
  is_read: boolean;
}

interface Participant {
  id: number;
  username: string;
  full_name: string;
  role: string;
  joined_at: string;
  last_read_at: string;
  is_online: boolean;
}

const FullPageChatRoom: React.FC<FullPageChatRoomProps> = ({ chatRoom, onBack }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [showParticipants, setShowParticipants] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadMessages = async (pageNum: number = 1, append: boolean = false) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.chat.getChatMessages(chatRoom.id, pageNum);
      
      if (response.success) {
        if (append) {
          setMessages(prev => [...response.messages.reverse(), ...prev]);
        } else {
          setMessages(response.messages.reverse());
        }
        setHasMore(response.has_more);
        setPage(pageNum);
        
        // Scroll to bottom for new messages
        if (!append) {
          setTimeout(scrollToBottom, 100);
        }
      } else {
        setError('Failed to load messages');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const loadParticipants = async () => {
    try {
      const response = await apiService.chat.getChatParticipants(chatRoom.id);
      if (response.success) {
        setParticipants(response.participants);
      }
    } catch (err) {
      console.error('Failed to load participants:', err);
    }
  };

  const sendMessage = async (messageType: 'text' | 'image' | 'file' = 'text', file?: File) => {
    if (!newMessage.trim() && !file) return;

    try {
      setSending(true);
      setError(null);

      const messageData: any = {
        message_type: messageType,
      };

      if (messageType === 'text') {
        messageData.content = newMessage.trim();
      } else if (file) {
        messageData.file = file;
      }

      const response = await apiService.chat.sendMessage(chatRoom.id, messageData);
      
      if (response.success) {
        setMessages(prev => [...prev, response.message]);
        setNewMessage('');
        setTimeout(scrollToBottom, 100);
        // Dispatch event for global widget
        window.dispatchEvent(new CustomEvent('chatMessageSent', { detail: { roomId: chatRoom.id } }));
      } else {
        setError('Failed to send message');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage('text');
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const messageType = file.type.startsWith('image/') ? 'image' : 'file';
      sendMessage(messageType, file);
    }
  };

  const loadMoreMessages = () => {
    if (hasMore && !loading) {
      loadMessages(page + 1, true);
    }
  };

  useEffect(() => {
    loadMessages();
    loadParticipants();
    // Mark messages as read when chat is opened
    apiService.chat.markMessagesRead(chatRoom.id).catch(err => {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to mark messages as read:', err);
      }
    });
  }, [chatRoom.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  return (
    <div className="full-page-chat-room">
      {/* Chat Header */}
      <div className="full-page-chat-header">
        <div className="d-flex align-items-center w-100">
          {onBack && (
            <Button
              variant="link"
              className="back-button me-2 d-md-none"
              onClick={onBack}
            >
              <i className="fas fa-arrow-left"></i>
            </Button>
          )}
          <div className="flex-grow-1">
            <h5 className="mb-0">{chatRoom.name}</h5>
            <small className="text-muted">{chatRoom.content_object}</small>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="link"
              className="header-button"
              onClick={() => setShowParticipants(true)}
              title="Participants"
            >
              <i className="fas fa-users"></i>
            </Button>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div className="full-page-chat-messages" ref={messagesContainerRef}>
        {loading && messages.length === 0 && (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-muted">Loading messages...</p>
          </div>
        )}

        {error && (
          <Alert variant="danger" className="m-3">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </Alert>
        )}

        {hasMore && (
          <div className="text-center py-2">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={loadMoreMessages}
              disabled={loading}
            >
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                'Load More Messages'
              )}
            </Button>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`full-page-message ${message.is_outgoing ? 'message-out' : 'message-in'} ${message.is_system_message ? 'system-message' : ''}`}
          >
            {!message.is_system_message && !message.is_outgoing && message.sender && (
              <div className="message-sender-name">
                {message.sender.full_name || message.sender.username}
              </div>
            )}
            
            <div className="message-content-wrapper">
              <div className="message-content">
                {message.message_type === 'text' || message.is_system_message ? (
                  <div className="message-text">{message.content}</div>
                ) : message.message_type === 'image' && message.file_url ? (
                  <img
                    src={message.file_url}
                    alt="Shared image"
                    className="message-image"
                  />
                ) : message.message_type === 'file' && message.file_url ? (
                  <a
                    href={message.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="message-file"
                  >
                    <i className="fas fa-file me-2"></i>
                    Download File
                  </a>
                ) : null}
              </div>
              <div className="message-time">
                {message.time_ago}
              </div>
            </div>
          </div>
        ))}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="full-page-chat-input">
        <Form onSubmit={handleSubmit}>
          <div className="input-group">
            <Button
              type="button"
              variant="link"
              className="input-button"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending}
              title="Attach file"
            >
              <i className="fas fa-paperclip"></i>
            </Button>
            
            <Form.Control
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              disabled={sending}
              className="message-input"
            />
            
            <Button
              type="submit"
              variant="primary"
              className="send-button"
              disabled={sending || !newMessage.trim()}
              title="Send message"
            >
              {sending ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <i className="fas fa-paper-plane"></i>
              )}
            </Button>
          </div>
        </Form>
        
        <input
          ref={fileInputRef}
          type="file"
          className="d-none"
          accept="image/*,.pdf,.doc,.docx"
          onChange={handleFileUpload}
        />
      </div>

      {/* Participants Modal */}
      <Modal show={showParticipants} onHide={() => setShowParticipants(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Participants</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {participants.map((participant) => (
            <div key={participant.id} className="d-flex align-items-center mb-3">
              <div className="participant-avatar me-3">
                <i className="fas fa-user"></i>
              </div>
              <div className="flex-grow-1">
                <h6 className="mb-0">
                  {participant.full_name || participant.username}
                </h6>
                <small className="text-muted">
                  {participant.role} • Joined {new Date(participant.joined_at).toLocaleDateString()}
                </small>
              </div>
              <div className="participant-status">
                <span className={`status-indicator ${participant.is_online ? 'online' : 'offline'}`}></span>
              </div>
            </div>
          ))}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default FullPageChatRoom;

